import { convertTimeToMMSS } from '@src/utility/timeFormat';
import { getSpeakerColorFromLabel } from '@src/utility/speakerUtils';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { getBeatKey } from './clipSequenceUtils';

import type { ReelOutline } from './types';
import type { ReelClipResults } from '@src/services/reelClipProducer/types';

type TranscriptSection = {
  beatKey: string;
  chapterTitle: string;
  chapterIndex: number;
  speaker: string | null;
  text: string;
  /** Cumulative offset within the reel (not original video time) */
  reelTime: number;
  duration: number;
};

type Props = {
  outline: ReelOutline;
  clipResults: ReelClipResults;
  excludedBeats: Set<string>;
};

/**
 * Builds a flat ordered list of transcript sections with reel-relative timestamps.
 * Only included beats are shown.
 */
function buildSections(
  outline: ReelOutline,
  clipResults: ReelClipResults,
  excludedBeats: Set<string>,
): TranscriptSection[] {
  const sections: TranscriptSection[] = [];
  let cumulativeTime = 0;

  for (const chapter of outline.chapters) {
    for (const beat of chapter.beats) {
      const key = getBeatKey(chapter, beat);
      if (excludedBeats.has(key)) continue;

      const result = clipResults.get(key);
      const selected = result
        ? result.options[result.selectedOptionIndex]
        : null;
      const clip = selected?.clipFragment;
      const duration = clip?.duration ?? beat.estimatedDuration;

      sections.push({
        beatKey: key,
        chapterTitle: chapter.title,
        chapterIndex: chapter.order - 1,
        speaker: clip?.speakerName || beat.speaker || null,
        text: selected?.text || beat.description,
        reelTime: cumulativeTime,
        duration,
      });

      cumulativeTime += duration;
    }
  }

  return sections;
}

/**
 * Splits a block of text into sentence-like lines for readability.
 * Splits on sentence-ending punctuation followed by a space, or on
 * natural clause boundaries (And, But, So, Because, etc.) when sentences are long.
 */
function splitIntoLines(text: string): string[] {
  // First split on sentence boundaries (. ? ! followed by space + uppercase or end)
  const sentencePattern = /(?<=[.!?])\s+(?=[A-Z])/g;
  const sentences = text.split(sentencePattern).filter(s => s.trim());

  // For very long sentences (>120 chars), further split on clause boundaries
  const result: string[] = [];
  for (const sentence of sentences) {
    if (sentence.length <= 120) {
      result.push(sentence);
      continue;
    }
    // Split on conjunctions/transitions that start a clause
    const clausePattern = /\s+(?=And |But |So |Because |However |Now |If |Or |Then |Having |In terms )/g;
    const parts = sentence.split(clausePattern).filter(s => s.trim());
    result.push(...parts);
  }

  return result.length > 0 ? result : [text];
}

const ReelTranscriptPanel: React.FC<Props> = ({
  outline,
  clipResults,
  excludedBeats,
}) => {
  const sections = useMemo(
    () => buildSections(outline, clipResults, excludedBeats),
    [outline, clipResults, excludedBeats],
  );

  if (sections.length === 0) {
    return (
      <PanelContainer>
        <EmptyState>No transcript sections to display.</EmptyState>
      </PanelContainer>
    );
  }

  // Track speaker changes to show/hide speaker header
  let lastSpeaker = '';
  let lastChapterTitle = '';

  return (
    <PanelContainer>
      <ScrollableArea>
        {sections.map((section) => {
          const showChapterHeader = section.chapterTitle !== lastChapterTitle;
          const speakerChanged = section.speaker !== lastSpeaker || showChapterHeader;
          lastChapterTitle = section.chapterTitle;
          lastSpeaker = section.speaker || '';

          const speakerColor = getSpeakerColorFromLabel(section.speaker);
          const lines = splitIntoLines(section.text);

          return (
            <React.Fragment key={section.beatKey}>
              {showChapterHeader && (
                <ChapterDivider>
                  Ch {section.chapterIndex + 1}: {section.chapterTitle}
                </ChapterDivider>
              )}
              {speakerChanged && section.speaker ? (
                <SpeakerHeader>
                  <SpeakerLabel $color={speakerColor}>
                    {section.speaker}
                  </SpeakerLabel>
                  <SpeakerTime>
                    {convertTimeToMMSS(Math.round(section.reelTime))}
                  </SpeakerTime>
                </SpeakerHeader>
              ) : (
                <TimestampRow>
                  {convertTimeToMMSS(Math.round(section.reelTime))}
                </TimestampRow>
              )}
              <ParagraphBlock>
                {lines.map((line, i) => (
                  <ParagraphLine key={i}>{line}</ParagraphLine>
                ))}
              </ParagraphBlock>
            </React.Fragment>
          );
        })}
      </ScrollableArea>
    </PanelContainer>
  );
};

ReelTranscriptPanel.displayName = 'ReelTranscriptPanel';

export default ReelTranscriptPanel;

// --- Styled Components ---

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--background);
  padding: 12px 16px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  font-size: 13px;
  color: var(--text-muted);
`;

const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ChapterDivider = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 0 4px;

  &:first-child {
    padding-top: 0;
  }
`;

const SpeakerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  margin-bottom: 2px;
  user-select: none;

  ${ChapterDivider} + & {
    margin-top: 4px;
  }
`;

const SpeakerLabel = styled.span<{ $color: string }>`
  color: ${p => p.$color};
  font-size: var(--text-xs, 0.75rem);
  font-weight: var(--font-weight-medium, 500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpeakerTime = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs, 0.75rem);
  font-weight: var(--font-weight-regular, 400);
  flex-shrink: 0;
`;

const TimestampRow = styled.div`
  text-align: right;
  color: var(--text-muted);
  font-size: var(--text-xs, 0.75rem);
  margin-top: -11px;
  margin-bottom: 5px;
  user-select: none;
`;

const ParagraphBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
`;

const ParagraphLine = styled.p`
  font-family: var(--font-primary, 'Inter', sans-serif);
  font-size: var(--text-sm, 0.875rem);
  line-height: var(--leading-text-sm, 1.25rem);
  color: var(--text-secondary);
  user-select: text;
  cursor: text;
  margin: 0;
`;
